"use client"

import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react"
import { MinusSmallIcon, PlusSmallIcon } from "@heroicons/react/24/outline"
import type { FaqItem } from "../_lib/types"

/**
 * Honest, real-data FAQ accordion.
 *
 * Clean bordered cards (no gray fill), semantic <dl>/<dt><h3>/<dd>, Heroicons
 * Plus/Minus, first item defaultOpen. Answers are PLAIN TEXT — no
 * dangerouslySetInnerHTML. Height animates via a CSS grid-rows transition
 * (250ms, brand easing) that collapses to instant under prefers-reduced-motion.
 * The rendered answer text MUST equal the FAQPage schema `text` verbatim.
 */
export function ProfileFAQ({ items, hideHeading = false }: { items: FaqItem[]; hideHeading?: boolean }) {
    if (!items || items.length === 0) return null

    return (
        <section aria-label="Frequently asked questions" className={hideHeading ? "" : "mt-16"}>
            <style>{`
                .ig-faq-panel { display: grid; transition: grid-template-rows 250ms cubic-bezier(0.22,1,0.36,1); }
                .ig-faq-panel > div { overflow: hidden; }
                @media (prefers-reduced-motion: reduce) { .ig-faq-panel { transition: none; } }
            `}</style>

            {!hideHeading && <h2 className="mb-6 text-2xl font-bold tracking-tight text-[#141414] [text-wrap:balance] sm:text-3xl">Questions, answered with real numbers</h2>}

            <dl className="space-y-3">
                {items.map((item, i) => (
                    <Disclosure as="div" key={item.question} defaultOpen={i === 0} className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(20,20,20,0.04),0_6px_18px_-10px_rgba(20,20,20,0.12)]">
                        {({ open }) => (
                            <>
                                <dt>
                                    <DisclosureButton className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(255,163,209)] focus-visible:ring-offset-0 sm:px-6 sm:py-5">
                                        <h3 className="text-[16px] font-semibold leading-7 text-[#141414] sm:text-[17px]">{item.question}</h3>
                                        <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center text-gray-500">
                                            {open ? <MinusSmallIcon aria-hidden="true" className="h-5 w-5" /> : <PlusSmallIcon aria-hidden="true" className="h-5 w-5" />}
                                        </span>
                                    </DisclosureButton>
                                </dt>
                                <DisclosurePanel static as="dd" className="ig-faq-panel" aria-hidden={!open} style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
                                    <div>
                                        <p className="px-5 pb-5 text-[15px] leading-7 text-gray-600 [text-wrap:pretty] sm:px-6 sm:pb-6">{item.answer}</p>
                                    </div>
                                </DisclosurePanel>
                            </>
                        )}
                    </Disclosure>
                ))}
            </dl>
        </section>
    )
}
